import type { EditableTestCase, PendingChanges } from "../model"

interface PendingEditState {
  pendingChanges: PendingChanges | null
  testCases: EditableTestCase[]
}

interface PendingAdditionState {
  pendingChanges: PendingChanges | null
  testCases: EditableTestCase[]
}

interface PendingDeletionState {
  pendingChanges: PendingChanges | null
  selectedTestCaseId: string | null
  testCases: EditableTestCase[]
}

function getNextPendingChanges<T extends PendingChanges["type"]>(
  type: T,
  data: Extract<PendingChanges, { type: T }>["data"],
): PendingChanges | null {
  if (data.length === 0) {
    return null
  }

  return { type, data } as Extract<PendingChanges, { type: T }>
}

export function getAcceptedPendingEditState(
  pendingChanges: PendingChanges | null,
  testCases: EditableTestCase[],
  testCaseId: string,
): PendingEditState | null {
  if (pendingChanges?.type !== "EDIT") {
    return null
  }

  const edit = pendingChanges.data.find(
    (candidate) => candidate.id === testCaseId,
  )
  if (!edit) {
    return null
  }

  return {
    pendingChanges: getNextPendingChanges(
      "EDIT",
      pendingChanges.data.filter((candidate) => candidate.id !== testCaseId),
    ),
    testCases: testCases.map((testCase) =>
      testCase.id === edit.id ? { ...testCase, ...edit } : testCase,
    ),
  }
}

export function getRejectedPendingEditState(
  pendingChanges: PendingChanges | null,
  testCaseId: string,
): PendingChanges | null {
  if (pendingChanges?.type !== "EDIT") {
    return null
  }

  return getNextPendingChanges(
    "EDIT",
    pendingChanges.data.filter((candidate) => candidate.id !== testCaseId),
  )
}

export function getAcceptedPendingAdditionState(
  pendingChanges: PendingChanges | null,
  testCases: EditableTestCase[],
  index: number,
): PendingAdditionState | null {
  if (pendingChanges?.type !== "ADD") {
    return null
  }

  const newTestCase = pendingChanges.data[index]
  if (!newTestCase) {
    return null
  }

  return {
    pendingChanges: getNextPendingChanges(
      "ADD",
      pendingChanges.data.filter(
        (_: unknown, pendingIndex) => pendingIndex !== index,
      ),
    ),
    testCases: [...testCases, newTestCase],
  }
}

export function getRejectedPendingAdditionState(
  pendingChanges: PendingChanges | null,
  index: number,
): PendingChanges | null {
  if (pendingChanges?.type !== "ADD") {
    return null
  }

  return getNextPendingChanges(
    "ADD",
    pendingChanges.data.filter(
      (_: unknown, pendingIndex) => pendingIndex !== index,
    ),
  )
}

/**
 * Staged additions left after `deletedIds` are removed, for a delete that spans saved rows and staged ones at once.
 */
export function getPendingChangesAfterDelete(
  pendingChanges: PendingChanges | null,
  deletedIds: Set<string>,
): PendingChanges | null {
  if (pendingChanges?.type !== "ADD") {
    return pendingChanges
  }

  return getNextPendingChanges(
    "ADD",
    pendingChanges.data.filter((testCase) => !deletedIds.has(testCase.id)),
  )
}

export function getAcceptedPendingDeletionState(
  pendingChanges: PendingChanges | null,
  testCases: EditableTestCase[],
  selectedTestCaseId: string | null,
  testCaseId: string,
): PendingDeletionState | null {
  if (pendingChanges?.type !== "DELETE") {
    return null
  }

  if (!pendingChanges.data.includes(testCaseId)) {
    return null
  }

  const nextTestCases = testCases.filter(
    (testCase) => testCase.id !== testCaseId,
  )

  return {
    pendingChanges: getNextPendingChanges(
      "DELETE",
      pendingChanges.data.filter((candidateId) => candidateId !== testCaseId),
    ),
    selectedTestCaseId:
      selectedTestCaseId === testCaseId
        ? (nextTestCases[0]?.id ?? null)
        : selectedTestCaseId,
    testCases: nextTestCases,
  }
}

export function getRejectedPendingDeletionState(
  pendingChanges: PendingChanges | null,
  testCaseId: string,
): PendingChanges | null {
  if (pendingChanges?.type !== "DELETE") {
    return null
  }

  return getNextPendingChanges(
    "DELETE",
    pendingChanges.data.filter((candidateId) => candidateId !== testCaseId),
  )
}
